
import React, { useEffect } from "react";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PixelInstallation } from "../PixelInstallation";
import { useDialogState } from "../context/DialogStateHooks";

interface InstallationContentProps {
  pixelId: string;
  onTestPixel: () => Promise<void>;
  onClose: () => void;
}

export function InstallationContent({ pixelId, onTestPixel, onClose }: InstallationContentProps) {
  const dialogState = useDialogState();
  
  // When mounting, log that we're in installation phase
  useEffect(() => {
    console.log("InstallationContent mounted for pixelId:", pixelId);
    
    // Reset the explicitly closed flag when entering installation phase
    // CRITICAL: Only reset if dialog is meant to be open
    if (dialogState.internalOpen) {
      console.log("Resetting explicitly closed flag on installation mount");
      dialogState.resetExplicitlyClosed();
    }
    
    return () => {
      console.log("InstallationContent unmounting for pixelId:", pixelId);
    };
  }, [pixelId, dialogState]);

  // Wrapped handlers with useCallback to maintain stable references
  const handleTestPixel = React.useCallback(async () => {
    console.log("Testing pixel:", pixelId);
    await onTestPixel();
  }, [pixelId, onTestPixel]);

  // Properly handle the close button click with improved cleanup sequence
  const handleClose = React.useCallback(() => {
    console.log("Installation close button clicked - performing complete cleanup");
    
    // CRITICAL FIX: First set explicitly closed to prevent auto-reopening
    dialogState.setExplicitlyClosed(true);
    
    // Synchronously set our internal state first
    dialogState.setInternalOpen(false);
    dialogState.setForceKeepOpen(false);
    
    // Then call the parent onClose to allow full state cleanup
    if (onClose) {
      onClose();
    }
  }, [onClose, dialogState]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Install Your Pixel</DialogTitle>
        <DialogDescription>
          Follow these instructions to install your tracking pixel.
        </DialogDescription>
      </DialogHeader>
      <div data-testid="installation-wrapper" className="installation-container py-2">
        <PixelInstallation 
          pixelId={pixelId}
          onTestPixel={handleTestPixel}
          onClose={handleClose}
        />
      </div>
    </>
  );
}
